import { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { useNarrativeErrorToast } from '@/pages/Narrative/hooks/useNarrativeErrorToast';
import {
  hasErrors,
  useNarrativeForm,
} from '@/pages/Narrative/hooks/useNarrativeForm';
import { useNarrativeTracking } from '@/pages/Narrative/hooks/useNarrativeTracking';
import { validateNarrativeForm } from '@/pages/Narrative/utils';
import { type NarrativeForm } from '@/pages/Narrative/utils/schema';
import { useTracking } from 'react-tracking';
import { NarrativeMiniValuatorCompanySelector } from './NarrativeMiniValuatorCompanySelector';
import {
  narrativeMiniValuatorAppendModifiedFields,
  type NarrativeMiniValuatorScreen,
} from '../utils';
import { NarrativeMiniValuatorFairValueInput } from './NarrativeMiniValuatorFairValueInput/NarrativeMiniValuatorFairValueInput';
import { Button, TextField, cn } from '@simplywallst/ui-core/next';
import { TimeseriesPlot } from '../../TimeseriesPlot';
import { NarrativeMiniValuatorGrowth } from './NarrativeMiniValuatorGrowth';
import ResizeSolid from '@simplywallst/ui-core/icons/ResizeSolid';
import RefreshOutline from '@simplywallst/ui-core/icons/RefreshOutline';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { useNarrativeMiniValuatorCalculations } from '../hooks/useNarrativeMiniValuatorCalculations';
import { NarrativeMiniValuatorPriceDiscount } from './NarrativeMiniValuatorPriceDiscount';
import { ReactNode, useRef, useState } from 'react';
import GuestRegistrationModal from '@/components/GuestRegistrationModal';
import useUser from '@/hooks/useUser';
import { NarrativeMiniValuatorDiscardProgressConfirmation } from './NarrativeMiniValuatorDiscardProgressConfirmation';
import { PastAnnualRevenueEarnings } from '@/pages/Narrative/types';

type Props = {
  company: NarrativeValuatorCompany;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  setScreen: (screen: NarrativeMiniValuatorScreen) => void;
  initialValues: NarrativeForm;
  onSubmit: (values: NarrativeForm) => void;
  isSubmitting: boolean;
  fullScreenPath: string;
  saveLabel: ReactNode;
  enableReinitialize: boolean;
};

export const NarrativeMiniValuatorForm = ({
  company,
  pastAnnualRevenueEarnings,
  initialValues,
  setScreen,
  onSubmit,
  isSubmitting,
  fullScreenPath,
  saveLabel,
  enableReinitialize,
}: Props) => {
  const { info, name, tickerSymbol } = company;
  const { trackEvent } = useTracking();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const discardCallbackRef = useRef<() => void>();
  const {
    data: { loggedIn },
  } = useUser();

  const {
    values,
    errors,
    setPartialValues,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    setFieldValue,
    hasErrors: hasFormErrors,
    resetForm,
    isDirty,
  } = useNarrativeForm({
    initialValues,
    validate: validateNarrativeForm,
    enableReinitialize,
  });
  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({ trackEvent, dirty, errors });

  const handleSubmit = () => {
    if (!loggedIn) return setShowRegistrationModal(true);

    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    onSubmit(values);

    setSubmitCount(0);
  };

  const {
    companyCurrencySymbol,
    tradingCurrencyIso,
    lastSharePrice,
    handleOnFairValueChange,
    handleOnRevenueChange,
    handleOnTimePeriodChange,
  } = useNarrativeMiniValuatorCalculations({
    company,
    pastAnnualRevenueEarnings,
    values,
    setPartialValues,
  });

  const isTouched = submitCount > 0 || Object.values(dirty).some(Boolean);
  return (
    <NarrativeMiniValuatorDiscardProgressConfirmation
      isOpen={showDiscardConfirmation}
      onDiscard={() => {
        setShowDiscardConfirmation(false);
        if (!discardCallbackRef.current) return;

        discardCallbackRef.current();
        discardCallbackRef.current = undefined;
      }}
      onCancel={() => {
        setShowDiscardConfirmation(false);
        discardCallbackRef.current = undefined;
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="grid p-x2"
        aria-invalid={hasFormErrors}
      >
        <NarrativeMiniValuatorCompanySelector
          onCompanySelect={() => {
            if (isDirty) {
              discardCallbackRef.current = () => setScreen('companySelect');
              return setShowDiscardConfirmation(true);
            }
            setScreen('companySelect');
          }}
          companyName={name}
          tickerSymbol={tickerSymbol}
          website={info?.url}
        />
        <div className="mt-x1 grid grid-cols-[130px_minmax(125px,auto)] gap-x1_5 rounded-md bg-surface-2 p-x1">
          <NarrativeMiniValuatorFairValueInput
            currencySymbol={companyCurrencySymbol}
            onChange={handleOnFairValueChange}
            value={values.fairValue}
          />

          <NarrativeMiniValuatorPriceDiscount
            currencyIso={tradingCurrencyIso}
            fairValue={values.fairValue}
            lastSharePrice={lastSharePrice}
            npvPerShare={company.analysisValue?.npvPerShare ?? null}
          />
        </div>

        <div className="mt-x1">
          <NarrativeMiniValuatorGrowth
            values={values}
            onRevenueChange={handleOnRevenueChange}
            onTimePeriodChange={handleOnTimePeriodChange}
          />
        </div>

        <div className="overflow-hidden">
          <TimeseriesPlot
            pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
            company={company}
            values={values}
          />
        </div>

        <div
          className={cn(
            'mt-x1 overflow-y-clip transition-[max-height,min-height,opacity] duration-500',
            isTouched || values.title
              ? 'max-h-x11 min-h-x9 opacity-100'
              : 'max-h-0 opacity-0'
          )}
        >
          <TextField
            label="Add summary"
            placeholder="What's your take on this stock?"
            value={values.title}
            onChange={(title) => setFieldValue('title', title)}
            error={errors.title}
          />
        </div>

        <div className="mt-x1 grid grid-cols-[1fr_repeat(3,auto)] justify-end gap-x1">
          <div>
            <Button
              href="https://www.youtube.com/watch?v=lbOjxgYSJ0A"
              rel="nofollow noopener"
              target="_blank"
              variant="subtle"
              size="medium"
              className="!px-x0_5"
              aria-label="Help"
              onClick={() => trackEvent({ action: 'help' })}
            >
              <InformationSolid />
            </Button>
          </div>
          <Button
            isDisabled={!isDirty}
            variant="secondary"
            size="medium"
            onClick={() => {
              trackEvent({ action: 'reset' });
              discardCallbackRef.current = resetForm;
              setShowDiscardConfirmation(true);
            }}
            className="!px-x0_5"
            aria-label="Reset form"
          >
            <RefreshOutline />
          </Button>
          <Button
            variant="secondary"
            href={narrativeMiniValuatorAppendModifiedFields({
              pathname: fullScreenPath,
              dirty,
              values,
            })}
            size="medium"
            onClick={() => trackEvent({ action: 'advanced' })}
            className="!pl-x1"
          >
            <ResizeSolid />
            Advanced
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            size="medium"
            onClick={() => trackEvent({ action: 'save' })}
          >
            {saveLabel}
          </Button>
        </div>
        {showRegistrationModal && (
          <GuestRegistrationModal
            canClose
            onClose={() => setShowRegistrationModal(false)}
            onLoginSuccess={() => setShowRegistrationModal(false)}
            onRegisterSuccess={() => setShowRegistrationModal(false)}
            removeWindowScroll
          />
        )}
      </form>
    </NarrativeMiniValuatorDiscardProgressConfirmation>
  );
};
